export const FAVORITES_KEY = 'siteFront.visitorFavorites.v1';
export const FAVORITES_EVENT = 'visitor-favorites-changed';

export interface FavoriteImage {
    id: number;
    hash: string;
    year: string;
    month: string;
    addedAt: number;
    collectionIds: string[];
}

export interface FavoriteCollection { id: string; name: string; createdAt: number; }
export interface VisitorFavorites { version: 1; images: FavoriteImage[]; collections: FavoriteCollection[]; }
export const emptyFavorites = (): VisitorFavorites => ({ version: 1, images: [], collections: [] });

export function readFavorites(): VisitorFavorites {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (raw === null) return emptyFavorites();
    const data = JSON.parse(raw);
    const validTime = (value: unknown) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
    if (data?.version !== 1 || !Array.isArray(data.images) || !Array.isArray(data.collections)) throw new Error('invalid');
    const ids = new Set<string>();
    const names = new Set<string>();
    for (const collection of data.collections) {
        if (!collection || typeof collection.id !== 'string' || !collection.id || ids.has(collection.id)
            || typeof collection.name !== 'string' || !collection.name.trim() || collection.name.length > 60
            || collection.name !== collection.name.trim() || names.has(collection.name.toLocaleLowerCase()) || !validTime(collection.createdAt)) {
            throw new Error('invalid');
        }
        ids.add(collection.id);
        names.add(collection.name.toLocaleLowerCase());
    }
    const imageIds = new Set<number>();
    for (const image of data.images) {
        if (!image || !Number.isSafeInteger(image.id) || image.id <= 0 || imageIds.has(image.id)
            || typeof image.hash !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(image.hash)
            || typeof image.year !== 'string' || !/^\d{4}$/.test(image.year)
            || typeof image.month !== 'string' || !/^(0[1-9]|1[0-2])$/.test(image.month)
            || !validTime(image.addedAt) || !Array.isArray(image.collectionIds)
            || image.collectionIds.some((id: unknown) => typeof id !== 'string' || !ids.has(id))
            || new Set(image.collectionIds).size !== image.collectionIds.length) {
            throw new Error('invalid');
        }
        imageIds.add(image.id);
    }
    return data;
}

export function updateFavorites(change: (data: VisitorFavorites) => void): VisitorFavorites {
    // Read again before each change so another tab's completed edits are preserved.
    const data = readFavorites();
    change(data);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(data));
    window.dispatchEvent(new Event(FAVORITES_EVENT));
    return data;
}

export function collectionName(data: VisitorFavorites, value: string, exceptId?: string): string {
    const name = value.trim();
    if (!name || name.length > 60) throw new Error('nameLength');
    if (data.collections.some(item => item.id !== exceptId && item.name.toLocaleLowerCase() === name.toLocaleLowerCase())) {
        throw new Error('duplicate');
    }
    return name;
}

export function favoriteUrl(image: FavoriteImage): string {
    const params = new URLSearchParams({ page: 'gallery', view: 'image', year: image.year, month: image.month, hash: image.hash, id: String(image.id), from: 'favorites' });
    const collection = new URLSearchParams(window.location.search).get('collection');
    if (collection) params.set('collection', collection);
    if (new URLSearchParams(window.location.search).get('unfiled') === '1') params.set('unfiled', '1');
    return `/?${params}`;
}
